import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, finalize, of, startWith, switchMap } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { CarService } from '../../../core/api/car.service';
import { CarAvailability } from '../../../core/models/car.model';
import { CarCardComponent } from '../car-card/car-card.component';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';
import { toApiDateTime } from '../../../shared/utils/date.utils';

@Component({
  selector: 'app-car-list',
  standalone: true,
  imports: [
    CarCardComponent,
    DatetimeInputComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './car-list.component.html',
  styleUrl: './car-list.component.scss',
})
export class CarListComponent {
  private readonly carService = inject(CarService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly query = signal('');

  readonly trimmedQuery = computed(() => this.query().trim().toLowerCase());

  /**
   * Групп-валидатор диапазона: ошибка `dateRange` проверяется ТОЛЬКО когда
   * оба поля заполнены и `end <= start`. Если хотя бы одно поле пустое —
   * `dateRange` снимается с `end` (поля необязательны по новому контракту).
   * Прочие ошибки контрола не затираются.
   */
  private readonly rangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const startControl = group.get('start');
    const endControl = group.get('end');
    if (!startControl || !endControl) {
      return null;
    }
    const start = startControl.value as Date | null;
    const end = endControl.value as Date | null;

    if (start && end) {
      if (end.getTime() <= start.getTime()) {
        const existing = endControl.errors ?? {};
        endControl.setErrors({ ...existing, dateRange: true });
      } else if (endControl.errors?.['dateRange']) {
        const { dateRange, ...rest } = endControl.errors;
        endControl.setErrors(Object.keys(rest).length ? rest : null);
      }
    } else if (endControl.errors?.['dateRange']) {
      // Хотя бы одно поле пустое — dateRange неактуален.
      const { dateRange, ...rest } = endControl.errors;
      endControl.setErrors(Object.keys(rest).length ? rest : null);
    }
    return null;
  };

  readonly form = this.fb.group(
    {
      start: this.fb.control<Date | null>(null),
      end: this.fb.control<Date | null>(null),
    },
    { validators: [this.rangeValidator] },
  );

  /**
   * Свободные авто по значениям формы. Поля необязательны: каждый параметр
   * передаётся на backend только если заполнен (через `CarService.getFreeCars`).
   *
   * `startWith(this.form.value)` триггерит начальный запрос сразу при
   * инициализации компонента — с пустой формой уходит `?start=&end=`, на что
   * backend возвращает «сегодня» (fallback по контракту). Без `startWith`
   * поток `valueChanges` не эмитит при init, и список остался бы пустым.
   *
   * `filter(() => this.form.valid)` блокирует запрос при ошибке `dateRange`
   * (оба заполнены и `end <= start`) — `availableCars` сохраняет прошлый
   * список, а `<app-datetime-input>` подсветит ошибку на поле `end`.
   */
  readonly availableCars = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.form.value),
      debounceTime(300),
      filter(() => this.form.valid),
      switchMap((v) => {
        this.loading.set(true);
        const start = v.start ? toApiDateTime(v.start) : undefined;
        const end = v.end ? toApiDateTime(v.end) : undefined;
        return this.carService.getFreeCars(start, end).pipe(
          catchError(() => of<CarAvailability[]>([])),
          finalize(() => this.loading.set(false)),
        );
      }),
    ),
    { initialValue: [] },
  );

  readonly filteredCars = computed<CarAvailability[]>(() => {
    const q = this.trimmedQuery();
    const cars = this.availableCars();

    if (!q) {
      return cars;
    }
    return cars.filter((car) => car.car_title.toLowerCase().includes(q) || car.license_plate.includes(q));
  });

  onSearchInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  clearQuery(): void {
    this.query.set('');
  }
}
